import { z } from "zod";

/** Connector catalog reads (FR-30, FR-32). Secrets never appear in any shape here. */

export const connectorNameParamsSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9_-]*$/i, "A connector name is letters, digits, hyphen or underscore."),
});
export type ConnectorNameParams = z.infer<typeof connectorNameParamsSchema>;

export const connectorsQuerySchema = z.object({
  includeInactive: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
  /**
   * T-51: `offered` (the default) lists only what a new connection in the caller's tenant may use;
   * `all` lists every connector, each marked with `offer`, for the screens that name an existing
   * connection's connector and for the settings that choose among them.
   */
  include: z.enum(["offered", "all"]).default("offered"),
});
export type ConnectorsQuery = z.infer<typeof connectorsQuerySchema>;

export const connectorVersionSchema = z.object({
  tag: z.string(),
  label: z.string().nullable(),
  isDefault: z.boolean(),
  releasedAt: z.string().nullable(),
});
export type ConnectorVersionView = z.infer<typeof connectorVersionSchema>;

export const connectorSummarySchema = z.object({
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  kind: z.string(),
  categories: z.array(z.string()),
  iconUrl: z.string().nullable(),
  version: z.string(),
  isActive: z.boolean(),
  isPreview: z.boolean(),
  pipelineTemplate: z.string(),
  isIncrementalSupported: z.boolean(),
  supportsIncrementalPerEntity: z.boolean(),
  /**
   * #1238: an incremental import of this connector re-reads whole days (a declared table replaces
   * a window of days, `shared/connectors/loadModes.ts` `declaresWindowReplace`, Mouseflow among
   * them), so it reads every day since the last import rather than only changes. Absent reads false.
   */
  readsWholeDays: z.boolean().optional(),
  /**
   * #2481: the connection's tables are reports the person adds (GA4), not tables the source lists,
   * so a draft's next step reads "add a report" (`shared/connectors/setupNeed.ts`). Absent reads false.
   */
  reportsConnector: z.boolean().optional(),
  versions: z.array(connectorVersionSchema),
  /**
   * T-51 (round 8): whether the caller's tenant offers this connector for a NEW connection, and if
   * not why (`shared/types/connectorAccess.ts`). `allowedForNew` is `offer === "offered"`. Absent
   * (a server older than T-51) reads as offered.
   */
  allowedForNew: z.boolean().optional(),
  offer: z.enum(["offered", "not_selected", "not_available", "hidden"]).optional(),
  /**
   * Round 10 (C1): for a Disabled connector, the sentence the server composed for this reader
   * (`CONNECTOR_REASON_*`). Null when the connector is offered.
   */
  offerReason: z.string().nullable().optional(),
});
export type ConnectorSummary = z.infer<typeof connectorSummarySchema>;
